export function num(v, d) {
    return typeof v === 'number' ? v : d;
}
export function str(v, d = '') {
    return typeof v === 'string' ? v : d;
}
/**
 * Read a block's shared "Font weight" (`weightField`) setting → a CSS font-weight string, or `''` when unset
 * (so the caller keeps its existing weight). Use as `fontWeight: readWeight(bs) || <existingDefault>`.
 */
export function readWeight(bs) {
    return typeof bs.weight === 'string' && bs.weight ? bs.weight : '';
}
/**
 * The leading from the shared `lineHeightField` — the seller's multiple, else `fallback`, the theme
 * expression the block painted before the field existed (`var(--aqx-theme-lh-body, 1.8)` and friends).
 *
 * An unset field returns `fallback` byte-for-byte, which is what keeps every published page still.
 * A stored value has to be a positive number: a document is JSON, so `0`, a string or `null` can all
 * come back, and `line-height:0` would stack every line of the paragraph on one row.
 */
/**
 * L'INTERLIGNE AUTOMATIQUE DU CORPS DE TEXTE — un ratio qui se resserre quand le texte grossit.
 *
 * Le thème donne UN ratio pour tout le corps de texte (`--aqx-theme-lh-body`, 1,8). Un ratio est
 * proportionnel par construction : il rend 23 px de ligne sur du 13 px — juste — et 43 px sur du 24 px,
 * où le blanc entre deux lignes dépasse la hauteur des lettres. C'est le défaut que la fondatrice a
 * signalé deux fois le 20/09/2026, la seconde en disant que le retour à la ligne laisse trop d'espace
 * « par défaut, dans tous les composants ».
 *
 * La mesure (sonde Playwright, 20/09) a tranché où était le défaut : les TITRES de section sont déjà
 * entre 1,15 et 1,30 et n'ont pas besoin d'être touchés. Le seul ratio lâche est celui du corps.
 *
 * ## La courbe
 *
 * L'interligne cesse d'être proportionnel et devient AFFINE : `S × (R − k) + c`, avec R le ratio du
 * thème, S la taille du texte, k = 0,92 et c = 12,88 px. Choisis pour que la courbe passe EXACTEMENT par
 * le réglage d'aujourd'hui à la taille de base du rôle (14 px × 1,8 = 25,2 px) et se resserre au-dessus :
 *
 *   14 px → 25,2 px (1,80, inchangé)   ·   20 px → 30,5 px (1,52)   ·   24 px → 34,0 px (1,42)
 *   32 px → 41,0 px (1,28)             ·   48 px → 55,1 px (1,15)
 *
 * Donc **aucun bloc laissé à la taille par défaut ne bouge d'un pixel** : seuls ceux que la vendeuse a
 * délibérément agrandis se resserrent, ce qui est exactement ce qu'elle demande.
 *
 * `max(1,05em, …)` est le plancher. Depuis que les curseurs de taille ne plafonnent plus, 100 px est
 * atteignable, et la droite y donnerait 1,01 — les jambages de la ligne du dessous toucheraient les
 * lettres de celle du dessus.
 *
 * Le `var()` garde le réglage de thème vivant : la vendeuse qui monte « Interligne du texte » à 2,0 dans
 * la typographie du thème décale toute la courbe vers le haut, elle ne la contourne pas.
 *
 * Écrit en `em`, donc calculé sur la taille de police de L'ÉLÉMENT qui porte la règle — c'est pour ça que
 * ça ne peut vivre que sur un bloc qui fixe sa propre taille, jamais sur `[data-aqx-page]`, où un `em`
 * serait résolu une fois contre les 16 px de la racine puis hérité en pixels figés par des titres de
 * toutes tailles.
 */
const LEADING_SLOPE_DROP = 0.92;
const LEADING_CONSTANT_PX = 12.88;
const LEADING_FLOOR = 1.05;
export const AUTO_BODY_LEADING = `max(${LEADING_FLOOR}em, 1em * var(--aqx-theme-lh-body, 1.8) ` +
    `- ${LEADING_SLOPE_DROP}em + ${LEADING_CONSTANT_PX}px)`;
/**
 * LE JUMEAU JS DE {@link AUTO_BODY_LEADING} — le ratio que cette courbe peint, en nombre.
 *
 * Il existe pour UNE raison : l'inspecteur doit pouvoir poser son curseur « Interligne » exactement là où
 * le texte est déjà. Sans lui, reprendre la main faisait sauter un paragraphe de 24 px de 34 px à 43 px
 * (la valeur du thème) — un contrôle qui déplace le texte à l'instant où on le touche est un contrôle
 * qu'on n'ose plus toucher, et ce dépôt a déjà payé ce défaut plusieurs fois.
 *
 * Les deux moitiés lisent les MÊMES constantes ci-dessus, et `blockRender.test.ts` les compare sur une
 * table de tailles : la courbe ne peut pas diverger entre ce que le canvas peint et ce que le panneau dit.
 *
 * `themeRatio` est la valeur du thème (le `var()` de la chaîne CSS) ; l'inspecteur n'a que le défaut sous
 * la main, ce qui est juste pour toute boutique qui n'a pas touché sa typographie.
 */
export function autoBodyLeadingRatio(sizePx, themeRatio = 1.8) {
    if (!Number.isFinite(sizePx) || sizePx <= 0)
        return themeRatio;
    const ratio = themeRatio - LEADING_SLOPE_DROP + LEADING_CONSTANT_PX / sizePx;
    return Math.max(LEADING_FLOOR, ratio);
}
export function readLineHeight(bs, fallback) {
    const v = bs.lineHeight;
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? String(v) : fallback;
}
/**
 * Block width from the shared width fields (% of the parent column). Emits nothing at 100 (or unset) so an
 * untouched block is byte-identical. Below 100 the block becomes a narrower box that is POSITIONED by its
 * alignment (left / center / right → margin-inline 0 auto / auto / auto 0), so a centred paragraph at 60%
 * sits centred, a right-aligned one hugs the right edge.
 *
 * `desktopWidth` alone (no `mobileAlign` passed) is the original behaviour: keyed ONLY to
 * `@container aqx-page` (present on both storefront and builder) so the phone keeps 100%.
 *
 * Pass `mobileAlign` and the block ALSO honours `mobileWidth` ({@link mobileWidthField}): the phone value
 * is the unscoped base rule, positioned by the MOBILE alignment. The two devices are independent, which is
 * what forces the `width:auto;margin-inline:0` reset — when only the phone is narrowed, the desktop rule
 * has to hand the width back, or the phone value leaks onto the wide screen (the trap already paid for on
 * `icon-with-text`). The returned string keeps the base rule BEFORE the container query on purpose: same
 * specificity, so whichever comes last would win.
 *
 * Since 24/09/2026 each device ALSO reads a pixel value first — `desktopWidthPx` / `mobileWidthPx`
 * ({@link widthPxField} / {@link mobileWidthPxField}). Since 05/10/2026 it is emitted as `width:<n>px;
 * max-width:100%`: the box IS the number (« for me the width is the box width », founder) — a `max-width`
 * let a short pill hug its words instead — and only the column it sits in can hold it back, a column that
 * the two-column sections now grow to hold it ({@link columnWidthPx}). A pixel value wins over the legacy
 * `%` share of the same device,
 * and `opts.legacyPercent: false` drops the `%` reading altogether (the `text` block, whose inspector no
 * longer carries the `%` pair).
 */
export function widthCss(cls, bs, align, mobileAlign, opts = {}) {
    // The `text` block passes `legacyPercent: false`: its inspector no longer shows the `%` pair, and a
    // value no control shows must not paint either — « here they are contradicting » (founder, 24/09/2026).
    const legacy = opts.legacyPercent ?? true;
    // Pixels first (`desktopWidthPx` / `mobileWidthPx`, 24/09/2026 — the box is the number, and `max-width:
    // 100%` keeps it inside a column narrower than itself), then the legacy share of the column
    // (`desktopWidth` / `mobileWidth`, %).
    const narrow = (px, pct, a) => {
        const inline = a === 'center' ? 'auto' : a === 'right' ? 'auto 0' : '0 auto';
        const p = num(px, 0);
        if (p > 0)
            return `width:${p}px;max-width:100%;margin-inline:${inline}`;
        const w = legacy ? num(pct, 100) : 100;
        if (!(w > 0 && w < 100))
            return '';
        return `width:${w}%;margin-inline:${inline}`;
    };
    const mobile = mobileAlign === undefined ? '' : narrow(bs.mobileWidthPx, bs.mobileWidth, mobileAlign);
    // Only the phone narrowed: hand BOTH spellings back on desktop, or the phone's cap leaks upward.
    const desktop = narrow(bs.desktopWidthPx, bs.desktopWidth, align) ||
        (mobile ? 'width:auto;max-width:none;margin-inline:0' : '');
    return ((mobile ? `.${cls}{${mobile}}` : '') +
        (desktop ? `@container aqx-page (min-width:750px){.${cls}{${desktop}}}` : ''));
}
/**
 * THE COLUMN FOLLOWS THE BOX (founder, 05/10/2026 — « the width we change should be the column width »).
 *
 * A text block's "Desktop width" is the box, in pixels. Inside a two-column section (the product page's
 * info column beside the gallery, image-with-text's copy beside the picture) the column used to be the
 * ceiling: past it the slider moved and nothing on the page did — « after I depasse 400px it doesn't
 * work anymore », ~400 px being the info column in the builder's preview, narrower than any desktop. So
 * the column now grows to hold the widest box its blocks ask for, and the neighbour (gallery, picture)
 * gives way, down to the floor in {@link columnMinWidth}.
 *
 * The widest `desktopWidthPx` among these blocks, nested blocks included, disabled ones skipped; 0 when
 * none asks. Read by the SECTION that owns the column — a child cannot widen its parent in CSS.
 */
export function columnWidthPx(blocks) {
    let widest = 0;
    for (const b of blocks ?? []) {
        if (b.disabled)
            continue;
        widest = Math.max(widest, num(b.settings.desktopWidthPx, 0), columnWidthPx(b.blocks));
    }
    return widest;
}
/** The neighbour column keeps at least this share of the row, so a gallery never shrinks to nothing. */
export const COLUMN_NEIGHBOUR_MIN_PCT = 20;
/**
 * The `min-width` of a column that must hold a {@link columnWidthPx} box: the box itself, or — when the
 * row cannot give that much — the row minus the neighbour's floor and the gap between the two. That edge
 * is the only limit left, and the builder's inspector names it when it is reached (`usePaintedWidth`).
 */
export function columnMinWidth(px, gap) {
    return `min(${px}px,calc(${100 - COLUMN_NEIGHBOUR_MIN_PCT}% - ${gap}))`;
}
/**
 * Top/bottom margins from a block's `marginTop`/`marginBottom` (px), RESPONSIVE: the desktop margins apply
 * inline, and the MOBILE margins (`mobileMarginTop`/`mobileMarginBottom`, default ~half desktop) are emitted
 * as `--aqx-mmt`/`--aqx-mmb` CSS vars. The single global rule in theme.css swaps margin-top/bottom to those
 * vars under ≤749px (both `@media` for the live storefront and `@container aqx-page` for the builder
 * device-preview). So every block that calls marginStyle becomes responsive with no per-block change.
 */
export function marginStyle(bs, defTop = 15, defBottom = 15) {
    const mt = num(bs.marginTop, defTop);
    const mb = num(bs.marginBottom, defBottom);
    // Mobile falls back to ~half the RESOLVED desktop margin (so a bumped desktop margin scales down on
    // mobile too) when the merchant hasn't set an explicit mobile value.
    const mmt = num(bs.mobileMarginTop, Math.round(mt / 2));
    const mmb = num(bs.mobileMarginBottom, Math.round(mb / 2));
    return {
        marginTop: `${mt}px`,
        marginBottom: `${mb}px`,
        '--aqx-mmt': `${mmt}px`,
        '--aqx-mmb': `${mmb}px`,
    };
}
/**
 * A scoped CSS rule hiding `.${cls}` on the OPPOSITE breakpoint per Shrine `visibility`. Emits BOTH a
 * `@media` (live storefront, viewport-based) and a `@container aqx-page` (builder device-preview, which
 * frames the page width without a viewport change) so it reflows in both. Returns '' for always-display.
 */
export function visibilityCss(cls, visibility) {
    if (visibility === 'desktop-hidden') {
        // Mobile only → hidden by DEFAULT, revealed only when NARROW. We must NOT hide via @media(min-width:750):
        // in the builder that keys off the editor WINDOW (wide), so it hides the section even inside a narrow
        // mobile preview frame, and a @container rule can't un-hide it. Default-hide + reveal-on-narrow (max-width)
        // lets @container reveal it inside the frame, while @media still handles real mobile viewports.
        return (`.${cls}{display:none!important}` +
            `@media(max-width:749px){.${cls}{display:block!important}}` +
            `@container aqx-page (max-width:749px){.${cls}{display:block!important}}`);
    }
    if (visibility === 'mobile-hidden') {
        // Desktop only → hide <750px.
        return (`@media(max-width:749px){.${cls}{display:none!important}}` +
            `@container aqx-page (max-width:749px){.${cls}{display:none!important}}`);
    }
    return '';
}
/**
 * Baseline "rich text prose" CSS scoped to `.${cls}`, re-establishing the element styling that the app's
 * Tailwind Preflight reset (both apps import `@tailwind base`) STRIPS from richtext OUTPUT: list markers +
 * indent (ul/ol/li), heading sizes/weight (h2/h3), blockquote, underline/strike, and — critically — the
 * link underline (Preflight sets `a{text-decoration:inherit}`, so richtext links otherwise render as
 * indistinguishable body text). Without this, a merchant's bullet list / heading / underline authored in a
 * Tiptap `richtext` field collapses to flat text on the storefront AND in the builder canvas (which also
 * loads Preflight) — only bold survives, because Preflight keeps `strong{font-weight:bolder}`.
 *
 * Scoped to the block class so section layout lists that RELY on the reset stay untouched. Kept in LOCKSTEP
 * with the editor's `.bld-rte__content` prose CSS (builder shell.css) and the RICHTEXT_ALLOW tag list —
 * same tags, same look — so what a seller authors is what renders (true WYSIWYG).
 */
export function richtextProseCss(cls) {
    const c = `.${cls}`;
    return (`${c} ul{list-style:disc;margin:.5em 0;padding-inline-start:1.5em}` +
        `${c} ol{list-style:decimal;margin:.5em 0;padding-inline-start:1.5em}` +
        `${c} li{margin:.25em 0}` +
        `${c} h2{font-size:1.5em;font-weight:700;line-height:1.3;margin:.6em 0 .3em}` +
        `${c} h3{font-size:1.25em;font-weight:700;line-height:1.3;margin:.6em 0 .3em}` +
        `${c} blockquote{margin:.6em 0;padding-inline-start:1em;` +
        `border-inline-start:3px solid color-mix(in srgb,currentColor 25%,transparent);font-style:italic}` +
        `${c} a{color:inherit;text-decoration:underline}` +
        `${c} u{text-decoration:underline}` +
        `${c} s{text-decoration:line-through}` +
        `${c} strong,${c} b{font-weight:700}` +
        `${c} em,${c} i{font-style:italic}`);
}
/** Map a Shrine color-scheme select value to the matching `--aqx-scheme-*` CSS var. */
export function schemeColor(value) {
    switch (value) {
        case 'accent-1':
        case 'accent-2':
            return 'var(--aqx-scheme-accent)';
        // 0019 §5b: outline-button follows the WIRED --aqx-theme-outline (falls back to scheme accent).
        case 'outline-button':
            return 'var(--aqx-theme-outline, var(--aqx-scheme-accent))';
        case 'background-1':
            return 'var(--aqx-scheme-bg)';
        case 'background-2':
            return 'var(--aqx-scheme-surface)';
        case 'text':
        default:
            return 'var(--aqx-scheme-text)';
    }
}
//# sourceMappingURL=blockRender.js.map